import { faLock, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { isBefore, parseISO } from 'date-fns';
import { findIndex } from 'lodash';
import { FC, useCallback, useMemo, useState } from 'react';
import Button from 'src/components/0100_button';
import ResponseBox from 'src/components/0100_response_box';
import PlayerSearch from 'src/components/0400_player_search';
import useAttendance from 'src/components/0500_logistics/dystopia_rising/hooks/useAttendance';
import {
  createModSignup,
  destroyModSignup,
} from 'src/graphql/mutations/events.graphql';
import {
  ICreateModSignupMutation,
  ICreateModSignupMutationVariables,
  IDestroyModSignupMutation,
  IDestroyModSignupMutationVariables,
} from 'src/graphql/mutations/events.graphql.types';
import useButtonStates from 'src/hooks/buttonStates/useButtonStates';
import useEventAttendee from 'src/hooks/events/useEventAttendee';
import useRootUser from 'src/hooks/players/useRootUser';
import { useMutation } from 'urql';

export type TModSignup = {
  id: number;
  createdAt: string;
  eventAttendee: {
    id: number;
    user: {
      id: number;
      fullName: string;
    };
  };
};

interface IProps {
  id: number;
  participationLimit?: number;
  modSignups?: TModSignup[];
  canManageMod?: boolean;
  eventAllowsMoreSignup: boolean;
  sectionAllowsMoreSignup: boolean;
  waitlistAllowed: boolean;
  eventHasEnded: boolean;
  modSignupIsOpen: boolean;
}

const ModSignups: FC<IProps> = ({
  id,
  participationLimit = 0,
  modSignups = [],
  canManageMod,
  waitlistAllowed,
  sectionAllowsMoreSignup,
  eventAllowsMoreSignup,
  eventHasEnded,
  modSignupIsOpen,
}) => {
  const { rootUserId } = useRootUser();
  const { eventAttendee } = useEventAttendee();
  const { buttonState } = useButtonStates();
  const { eventId } = useParams({ strict: false });
  const [isSafetyLockActive, setIsSafetyLockActive] = useState(true);
  const [kickedSignupIds, setKickedSignupIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [, signup] = useMutation<
    ICreateModSignupMutation,
    ICreateModSignupMutationVariables
  >(createModSignup);
  const [, kick] = useMutation<
    IDestroyModSignupMutation,
    IDestroyModSignupMutationVariables
  >(destroyModSignup);
  const handleAdd = useCallback(
    ({ playerId }: { playerId: number }) => {
      setError(null);
      signup({
        modId: id,
        playerId,
      }).then(res => {
        if (res.data?.createModSignup?.error) {
          setError(res.data.createModSignup.error);
        }
      });
    },
    [id, signup],
  );
  const sortedModSignups = useMemo(
    () =>
      modSignups.sort((a, b) =>
        isBefore(parseISO(a.createdAt), parseISO(b.createdAt)) ? -1 : 1,
      ),
    [modSignups],
  );
  const modSignup = useMemo(
    () => modSignups.find(x => x.eventAttendee.user.id === rootUserId),
    [modSignups, rootUserId],
  );
  const inWaitlist = useMemo(
    () =>
      modSignup &&
      findIndex(
        sortedModSignups,
        x => x.eventAttendee.user.id === rootUserId,
      ) >= participationLimit,
    [modSignup, sortedModSignups, rootUserId, participationLimit],
  );

  const modParticipationExceeded = useMemo(
    () => !waitlistAllowed && modSignups.length >= participationLimit,
    [modSignups, participationLimit, waitlistAllowed],
  );
  const enteringWaitlist = useMemo(
    () => waitlistAllowed && modSignups.length >= participationLimit,
    [modSignups, participationLimit, waitlistAllowed],
  );
  const allowsSignup = useMemo(
    () =>
      !modSignup &&
      !modParticipationExceeded &&
      eventAllowsMoreSignup &&
      sectionAllowsMoreSignup &&
      !eventHasEnded &&
      modSignupIsOpen,
    [
      modSignup,
      modParticipationExceeded,
      eventAllowsMoreSignup,
      sectionAllowsMoreSignup,
      eventHasEnded,
      modSignupIsOpen,
    ],
  );
  const signupButtonErrorMessage = useMemo(() => {
    if (eventHasEnded) return 'Game has ended';
    if (!modSignupIsOpen) return 'Signup is not yet open';
    if (modParticipationExceeded) return 'Mod Full';
    if (!sectionAllowsMoreSignup) return 'Section Signup Limit Exceeded';
    if (!eventAllowsMoreSignup) return 'Global Signup Limit Exceeded';
  }, [
    eventHasEnded,
    modSignupIsOpen,
    modParticipationExceeded,
    sectionAllowsMoreSignup,
    eventAllowsMoreSignup,
  ]);

  return (
    <div className="w-full p-4 pt-0">
      <table className="table-fixed border-collapse w-full">
        <thead className="text-juno-gray-200">
          <tr>
            <th className="border border-juno-gray-700 p-1 w-16">Pos</th>
            <th className="border border-juno-gray-700 p-1 px-2 text-left">
              <div className="flex justify-between items-center">
                <div>Player</div>
                {canManageMod && (
                  <FontAwesomeIcon
                    icon={isSafetyLockActive ? faLock : faUnlock}
                    className="fa-fw"
                    onClick={() => setIsSafetyLockActive(x => !x)}
                  />
                )}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {modSignups.length === 0 && (
            <tr>
              <td
                className="border border-juno-gray-700 opacity-50 italic p-1 px-2"
                colSpan={2}
              >
                No Signups Yet
              </td>
            </tr>
          )}
          {sortedModSignups
            .filter(x => !kickedSignupIds.includes(x.id))
            .map((x, i) => (
              <tr
                key={x.id}
                className={clsx(
                  x.eventAttendee.user.id === rootUserId &&
                    'white-box text-juno-gray-900',
                )}
              >
                <td
                  className={clsx(
                    'border border-juno-gray-700 p-1 text-center font-bold',
                    i >= participationLimit
                      ? 'border-l-4 border-l-juno-orange-200  text-juno-orange-200'
                      : 'border-l',
                  )}
                >
                  {i >= participationLimit
                    ? `W${i + 1 - participationLimit}`
                    : i + 1}
                </td>
                <td className="border border-juno-gray-700 p-1 px-2 whitespace-nowrap overflow-hidden relative">
                  #{x.eventAttendee.user.id} {x.eventAttendee.user.fullName}
                  {canManageMod && (
                    <button
                      type="button"
                      className={clsx(
                        'absolute right-2 top-1 text-sm uppercase rounded bg-juno-orange-200 text-juno-gray-50 px-2 py-0.5',
                        'transition-all duration-300',
                        isSafetyLockActive ? 'opacity-0' : 'opacity-100',
                      )}
                      onClick={() => {
                        if (isSafetyLockActive) return;
                        setKickedSignupIds(kickedSignupIds => [
                          ...kickedSignupIds,
                          x.id,
                        ]);
                        kick({ modSignupId: x.id });
                        setIsSafetyLockActive(true);
                      }}
                    >
                      Kick
                    </button>
                  )}
                </td>
              </tr>
            ))}
          {(canManageMod || eventAttendee) && (
            <tr>
              <td colSpan={2} className="border border-juno-gray-700 p-2">
                {canManageMod && (
                  <PlayerSearch
                    playersOnly
                    eventId={Number(eventId)}
                    title="Queue Player"
                    placeholder="Search Attendee..."
                    onSearchResultClick={({ playerId }) =>
                      playerId && handleAdd({ playerId })
                    }
                  />
                )}
                {!modSignup && eventAttendee && (
                  <Button
                    defaultLabel="Sign Up"
                    state={buttonState({
                      isDirty: true,
                      isHighlight: true,
                      isValid: allowsSignup,
                    })}
                    stateLabel={{
                      loading: 'Signing Up...',
                      disabled: signupButtonErrorMessage,
                      enabled: enteringWaitlist ? 'Enter Waitlist' : 'Sign Up',
                    }}
                    onClick={() => handleAdd({ playerId: rootUserId })}
                  />
                )}
                {modSignup && (
                  <div className="flex items-center gap-2">
                    <div>
                      {inWaitlist
                        ? "You're in Waitlist. Fingers crossed!"
                        : "You're in. LFG!"}
                    </div>
                    {!eventHasEnded && (
                      <button
                        type="button"
                        className="text-juno-orange-200 underline"
                        onClick={() => {
                          if (isSafetyLockActive) {
                            setIsSafetyLockActive(false);
                          } else {
                            kick({ modSignupId: modSignup.id });
                            setIsSafetyLockActive(true);
                          }
                        }}
                      >
                        {isSafetyLockActive
                          ? 'Withdraw?'
                          : 'Confirm withdrawal!'}
                      </button>
                    )}
                    {!isSafetyLockActive && (
                      <button
                        type="button"
                        className="text-juno-cyan-200 brightness-150 underline"
                        onClick={() => setIsSafetyLockActive(true)}
                      >
                        NVM!
                      </button>
                    )}
                  </div>
                )}

                {error && <ResponseBox type="error">{error}</ResponseBox>}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ModSignups;
